import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { getScrollBarStyles } from '../../../../config/styles';
import { PrimaryButton } from '../../../Button';
import { ICohortInspectFieldFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import ThinDivider from '../../../ThinDivider';
import isEqual from 'lodash/isEqual';
import SearchInput from '../../../Form/SearchInput';
import CheckboxDropdownOption from '../../../Dropdown/CheckboxDropdownOption';

interface IProps {
  fields: ICohortInspectFieldFieldsFragment[];
  selectedFieldKeys: string[];
  onSubmit: (newSelection: string[]) => void;
}

const OptionsContainer = styled.div`
  max-height: 185px;
  overflow-y: auto;
  ${getScrollBarStyles(2)}
`;
const SubmitAction = styled(PrimaryButton)`
  width: 100%;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
`;

const UserInspectViewFieldsMenu = ({
  fields,
  selectedFieldKeys,
  onSubmit,
}: IProps) => {
  const [selection, setSelection] = useState(selectedFieldKeys);
  const [searchTerm, setSearchTerm] = useState('');

  const options: ICohortInspectFieldFieldsFragment[] = useMemo(() => {
    if (!fields?.length) {
      return [];
    }

    return (
      fields
        // filter out all nested fields
        .filter((field) => !field.nestedFieldMetadata)
        // apply search term
        .filter((field) => {
          if (searchTerm.length > 0) {
            return field.name.toLowerCase().includes(searchTerm.toLowerCase());
          }

          return true;
        })
        // put all fact fields on top of the list
        .sort((fieldA, fieldB) =>
          fieldA.isFactField === fieldB.isFactField
            ? 0
            : fieldA.isFactField
              ? -1
              : 1,
        )
    );
  }, [fields, searchTerm]);

  const toggleAll = () => {
    setSelection((prevState) =>
      prevState.length === options.length
        ? []
        : options.map((option) => option.key),
    );
  };
  const toggleOption = (value: string, checked: boolean) => {
    setSelection((prevState) => {
      if (checked) {
        return prevState.filter((stateValue) => stateValue !== value);
      } else {
        return [...prevState, value];
      }
    });
  };

  const handleSubmit = () => {
    onSubmit(selection);
  };

  const allIsChecked =
    options.length > 0 && selection.length === options.length;
  const submitDisabled = isEqual(selectedFieldKeys, selection);

  return (
    <>
      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={'Search Fields'}
      />
      <CheckboxDropdownOption
        label="All"
        isChecked={allIsChecked}
        onClick={toggleAll}
        isDisabled={!options.length}
      />
      <ThinDivider layout="horizontal" size="full" />
      <OptionsContainer>
        {options.map((option) => {
          const { key, name } = option;
          const checked = selection.includes(key);

          return (
            <CheckboxDropdownOption
              key={`field-${key}`}
              label={name}
              isChecked={checked}
              onClick={() => toggleOption(key, checked)}
            />
          );
        })}
      </OptionsContainer>
      <SubmitAction
        type="button"
        onClick={handleSubmit}
        disabled={submitDisabled}
        trackContext="user inspect"
        trackAction="confirm"
      >
        Confirm
      </SubmitAction>
    </>
  );
};

export default UserInspectViewFieldsMenu;
